import * as d3 from 'd3'
import { CSSProperties, Component, MouseEvent } from 'react'

import { forexDomains } from '../futures/utils'
import { generateColorScale as gcs } from '../futures/utils'
import { GradientDefinition } from '../shared/gradients'

var generateColorScale = gcs.bind(null, forexDomains)
const redesignEnabled = !!FinvizSettings.hasRedesignEnabled

var hexToRgba = (color: string, opacity: number) => {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color) as string[],
    r = parseInt(result[1], 16),
    g = parseInt(result[2], 16),
    b = parseInt(result[3], 16)
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

interface Bar {
  label: string
  value: number
  link?: string
}

interface BarChartProps {
  width: number
  period: 'd' | 'w' | 'm' | 'mtd' | 'q' | 'hy' | 'y' | 'ytd'
  data: Bar[]
  onClick?: (bar: Bar) => void
  gradient?: GradientDefinition
}

class BarChart extends Component<BarChartProps> {
  render() {
    var bars = this.props.data
    var labels = bars.map(function (d) {
      return d.label
    })

    var barWidth = 65,
      labelHeight = 20,
      gap = 1,
      width = (barWidth + gap * 2) * bars.length,
      height = 220,
      halfHeight = (220 - labelHeight) / 2

    var y = d3
      .scaleLinear()
      .domain([0, Math.ceil(d3.max(bars, (d: any) => Math.abs(d.value))!)])
      .range([0, halfHeight])
      .nice(6)

    var yAxisTicks = y.ticks(6).filter((tick: any) => tick > 0)

    var x = d3
      .scaleBand()
      .domain(labels)
      .range([0, (barWidth + 2 * gap) * bars.length])

    var colorScale = generateColorScale(this.props.period, this.props.gradient)

    // so React won't reorder our elements
    bars = bars.slice().sort(function (a: Bar, b: Bar) {
      if (a.label < b.label) {
        return -1
      }
      if (a.label > b.label) {
        return 1
      }
      return 0
    })

    var header =
      {
        d: '1 Day',
        w: '1 Week',
        m: '1 Month',
        mtd: 'Month to date',
        q: '3 Month',
        hy: 'Half year',
        y: '1 Year',
        ytd: 'Year to Date',
      }[this.props.period] + ' Relative Performance'

    return (
      <div
        className="vertical-bar-chart fv-vertical-bar-chart"
        style={{ margin: redesignEnabled ? undefined : '0 50px', width }}
      >
        <h1>{header}&nbsp;&nbsp;[USD]</h1>
        <div style={{ position: 'relative', height: height - 1 }}>
          {yAxisTicks.map((tick: any) => (
            <div
              className="bar-chart_tick"
              style={{ position: 'absolute', left: 0, top: halfHeight - Math.round(y(tick)) }}
              key={tick}
            >
              <div>{tick.toFixed(2)}%</div>
            </div>
          ))}

          {yAxisTicks.map((tick: any) => (
            <div
              className="bar-chart_tick"
              style={{ position: 'absolute', left: 0, top: halfHeight + labelHeight + Math.round(y(tick)) }}
              key={tick}
            >
              <div>-{tick.toFixed(2)}%</div>
            </div>
          ))}

          {bars.map((bar) => {
            var height = Math.round(y(Math.abs(bar.value)))
            var color = colorScale(bar.value)
            var style: CSSProperties = {
              position: 'absolute',
              top: bar.value >= 0 ? halfHeight - height : halfHeight + labelHeight,
              left: x(bar.label),
              height: height,
              width: barWidth,
              background: hexToRgba(color, 0.2),
              ...(redesignEnabled && {
                borderTopRightRadius: bar.value >= 0 ? 2 : 0,
                borderTopLeftRadius: bar.value >= 0 ? 2 : 0,
                borderBottomRightRadius: bar.value >= 0 ? 0 : 2,
                borderBottomLeftRadius: bar.value >= 0 ? 0 : 2,
              }),
            }
            var valueStyle = {
              top: bar.value > 0 ? -14 : 'auto',
              bottom: bar.value < 0 ? -14 : 'auto',
              ...(!redesignEnabled && {
                color,
              }),
            }
            var labelStyle = {
              background: color,
              bottom: bar.value >= 0 ? -labelHeight : height,
              ...(redesignEnabled && {
                borderTopRightRadius: bar.value >= 0 ? 0 : 2,
                borderTopLeftRadius: bar.value >= 0 ? 0 : 2,
                borderBottomRightRadius: bar.value >= 0 ? 2 : 0,
                borderBottomLeftRadius: bar.value >= 0 ? 2 : 0,
                ...(bar.value === 0 && {
                  borderRadius: 2,
                }),
              }),
            }
            return (
              <div
                className={'rect ' + (bar.link ? 'is-clickable' : '')}
                key={bar.label}
                style={style}
                onClick={this._onClick.bind(this, bar)}
              >
                <div className={'value'} style={valueStyle}>
                  {bar.value.toFixed(2)}%
                </div>
                <div className="label" style={labelStyle}>
                  {bar.label}
                </div>
              </div>
            )
          })}
        </div>
        <br />
        <br />
      </div>
    )
  }

  _onClick = (row: Bar, e: MouseEvent) => {
    e.preventDefault()
    this.props.onClick && this.props.onClick(row)
  }
}

export default BarChart
